import { useCallback, useMemo } from 'react';
import {
  Link,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowLeft,
  Link as LinkIcon,
  LogOut,
  MessageSquare,
  Search,
  Settings,
  Sparkles,
} from 'lucide-react';
import { HStack } from '@components/base/layout';
import { Text, Tooltip } from '@components/base/display';
import { Button } from '@components/base/actions';
import { SessionSwitcher } from '@/shared/session/SessionSwitcher';
import { useClaimFlowAuthStore } from '../auth/store';
import { claimflowApiClient } from '../api/claimflowApiClient';
import { commentsService } from '../api/commentsService';
import { useCommentsPanelStore } from './commentsPanelStore';
import { useAiChatStore } from './aiChatStore';
import { useClaimSearchStore } from './claimSearchStore';
import { CLAIM_SEARCH_CONFIG } from './claimSearchConfig';

interface CommentsTriggerProps {
  ses: number;
  idSin: string;
}

function CommentsTrigger({ ses, idSin }: Readonly<CommentsTriggerProps>) {
  const open = useCommentsPanelStore((s) => s.open);
  const toggle = useCommentsPanelStore((s) => s.toggle);

  const { data: counter } = useQuery({
    queryKey: ['claimflow', 'comments-counter', idSin],
    queryFn: () => commentsService.fetchCommentCounter(ses, idSin),
    refetchInterval: open ? false : 30_000,
  });

  const total = counter?.total ?? 0;
  const unread = counter?.unread ?? 0;

  return (
    <button
      type="button"
      onClick={toggle}
      aria-label="Ouvrir les commentaires"
      aria-expanded={open}
      className="relative inline-flex items-center gap-1.5 px-2 py-1.5 rounded-md text-secondary-600 hover:text-primary-700 hover:bg-secondary-100 transition-colors"
    >
      <span className="relative inline-flex">
        <MessageSquare className="w-5 h-5" />
        {unread > 0 && (
          <span
            className="absolute -top-1.5 -right-1.5 inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full bg-error-500 text-white font-bold text-[10px] leading-none"
            data-testid="comments-trigger-unread-badge"
          >
            {unread}
          </span>
        )}
      </span>
      <Text as="span" size="sm" color="muted">
        {total}
      </Text>
    </button>
  );
}

/** The `boby-ws2-gs-def-user-login` proxy envelope (only what is read here). */
type DefUserLoginResponse =
  | {
      data?: {
        data?: { CD_UTIL_SESSION?: string | number | null; CD_UTIL?: string | number | null }[];
      };
    }
  | undefined;

function AiChatTrigger() {
  const open = useAiChatStore((s) => s.open);
  const toggle = useAiChatStore((s) => s.toggle);

  return (
    <button
      type="button"
      onClick={toggle}
      aria-label="Ouvrir l'assistant IA"
      aria-expanded={open}
      className="relative inline-flex items-center gap-1.5 px-2 py-1.5 rounded-md text-secondary-600 hover:text-primary-700 hover:bg-secondary-100 transition-colors"
    >
      <Sparkles className="w-5 h-5" />
    </button>
  );
}

export function ClaimFlowHeader() {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { tenantSlug, taskId: taskIdParam } = useParams<{ tenantSlug: string; taskId?: string }>();
  const navigate = useNavigate();
  const openClaimSearch = useClaimSearchStore((s) => s.open);
  const user = useClaimFlowAuthStore((state) => state.user);
  const logout = useClaimFlowAuthStore((state) => state.logout);
  const handleLogout = useCallback(async () => {
    await logout();
    void navigate(`/claimflow/${tenantSlug ?? ''}`, { replace: true });
  }, [logout, navigate, tenantSlug]);
  const spoofedSessionId = useClaimFlowAuthStore((s) => s.spoofedSessionId);
  const setSpoofedSessionId = useClaimFlowAuthStore((s) => s.setSpoofedSessionId);
  const availableSessions = useClaimFlowAuthStore((s) => s.availableSessions);
  const setCdUtil = useClaimFlowAuthStore((s) => s.setCdUtil);
  // The claim screen, with a task (`/task/:taskId`) or without one (`/task`,
  // opened from the claim search — mahhp 22861).
  const isDetail = /\/task(\/|$)/.test(location.pathname);

  // The task id comes from the `:taskId` route param — also parsed from the
  // pathname, for a header rendered outside that route — or from `?task=`.
  // Absent on a task-less claim screen.
  const taskIdMatch = /\/task\/([^/]+)/.exec(location.pathname);
  const taskIdQuery = searchParams.get('task');
  const taskId =
    taskIdParam ??
    taskIdMatch?.[1] ??
    (taskIdQuery === null || taskIdQuery === '' ? undefined : taskIdQuery);
  const idSin = searchParams.get('id_sin');
  // Comments + AI chat are claim-level (`id_sin`): shown with or without a task.
  const showCommentsTrigger = isDetail && idSin && spoofedSessionId != null;

  const handleSessionConfirm = useCallback((sessionId: number) => {
    return claimflowApiClient.get<DefUserLoginResponse>(
      '/claimflow/erp/registries/veos/endpoints/boby-ws2-gs-def-user-login',
      { params: { SES: sessionId } },
    ).then((response) => {
      const items = response.data?.data?.data;
      const userRecord = Array.isArray(items) ? items[0] : undefined;
      if (userRecord) {
        const cdUtil = String(userRecord.CD_UTIL_SESSION ?? userRecord.CD_UTIL ?? '');
        if (cdUtil) {
          setCdUtil(cdUtil);
        }
      }
    }).catch(() => {
      // Fire-and-forget: login endpoint failure should not block the UI
    });
  }, [setCdUtil]);

  const sessionOptions = useMemo(
    () => availableSessions.map((s) => ({
      id: s.session,
      label: String(s.lib ?? s.session),
    })),
    [availableSessions],
  );

  const handleSessionSwitch = useCallback((sessionId: number, label: string) => {
    setSpoofedSessionId(sessionId, label);
    void handleSessionConfirm(sessionId).finally(() => {
      window.location.reload();
    });
  }, [setSpoofedSessionId, handleSessionConfirm]);

  return (
    <header className="w-full bg-white border-b border-secondary-200">
      <div className="flex items-center justify-between px-6 h-14">
        {/* Left: Logo + app name */}
        <HStack gap={3} align="center">
          <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center">
            <span className="text-white font-bold text-sm">C</span>
          </div>
          <Text as="span" weight="bold" size="lg" color="default">
            ClaimFlow
          </Text>
          <LinkIcon className="w-4 h-4 text-secondary-400" />
          <Text as="span" size="sm" color="muted">
            Gestion des sinistres automobiles
          </Text>
          {/* Claim search (mahhp 22861) — always visible. */}
          <div className="h-5 w-px bg-secondary-200" />
          <Tooltip content={CLAIM_SEARCH_CONFIG.text.trigger} position="bottom">
            <Button
              variant="ghost"
              size="sm"
              leftIcon={<Search className="w-4 h-4" />}
              onClick={openClaimSearch}
              aria-label={CLAIM_SEARCH_CONFIG.text.trigger}
            />
          </Tooltip>
        </HStack>

        {/* Right section */}
        <HStack gap={4} align="center">
          {/* Back link + task info (detail only; the task chip only with a task) */}
          {isDetail && (
            <>
              <div className="h-5 w-px bg-secondary-200" />
              <Link
                to={`/claimflow/${tenantSlug ?? ''}`}
                className="inline-flex items-center gap-1.5 text-sm text-secondary-600 hover:text-primary-700 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                Retour
              </Link>
              {taskId && (
                <>
                  <div className="h-5 w-px bg-secondary-200" />
                  <Text as="span" size="sm" color="muted">
                    Tâche <span className="font-mono">{taskId}</span>
                  </Text>
                </>
              )}
              {showCommentsTrigger && (
                <>
                  <CommentsTrigger ses={spoofedSessionId} idSin={idSin} />
                  <AiChatTrigger />
                </>
              )}
            </>
          )}

          {/* Session + user info + logout (always visible) */}
          {user && (
            <>
              <div className="h-5 w-px bg-secondary-200" />
              <Link
                to={`/claimflow/${tenantSlug ?? ''}/config`}
                className="inline-flex items-center gap-1.5 text-sm text-secondary-600 hover:text-primary-700 transition-colors"
              >
                <Settings className="w-4 h-4" />
                Configuration
              </Link>
              <div className="h-5 w-px bg-secondary-200" />
              <SessionSwitcher
                sessions={sessionOptions}
                activeSessionId={spoofedSessionId}
                onSessionChange={handleSessionSwitch}
              />
              <Text as="span" size="sm" color="muted">
                {user.name}
              </Text>
              <Button
                variant="ghost"
                size="sm"
                leftIcon={<LogOut className="w-4 h-4" />}
                onClick={handleLogout}
                aria-label="Se déconnecter"
              />
            </>
          )}
        </HStack>
      </div>
    </header>
  );
}
